import React, { useMemo } from 'react';

import { Helmet } from 'react-helmet';
import { useParams } from 'react-router-dom';

import { Alert } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { useGetAccount } from '~/hooks/apis/offers/useGetAccount';
import { useGetAccountOfferIds, useGetAccountOpportunities } from '~/hooks/apis/offers/useGetOffer';

import { toSortableTimestamp } from '~/services/TimeService';

import { formatSyncedDate, formatSyncedDay } from '../Assets/utils';

import AccountListPageHeader from './AccountListPageHeader';
import AccountOpportunityName from './AccountOpportunityName';
import { OFFER_LINKS_UNAVAILABLE_MESSAGE, sortOpportunitiesByLatestDate } from './accountRelatedModel';

const sharedOpts: Partial<GridColDef<App.OpportunityPage>> = {
  filterable: false,
  hideable: false,
  disableColumnMenu: true,
};

const dateOpts: Partial<GridColDef<App.OpportunityPage>> = {
  ...sharedOpts,
  type: 'number',
  align: 'left',
  headerAlign: 'left',
};

function getAutoRowHeight() {
  return 'auto' as const;
}

function getColumns(offerIds?: Set<string>): Array<GridColDef<App.OpportunityPage>> {
  return [
    {
      field: 'name',
      headerName: 'Opportunity Name',
      flex: 2,
      minWidth: 240,
      valueGetter: (_value, row) => row.opportunity_info?.name,
      renderCell: ({ row }) => <AccountOpportunityName hasOffer={!!offerIds?.has(row.id)} opportunity={row} />,
      ...sharedOpts,
    },
    {
      field: 'stage',
      headerName: 'Stage',
      flex: 1,
      minWidth: 120,
      valueGetter: (_value, row) => row.opportunity_info?.stagename,
      ...sharedOpts,
    },
    {
      field: 'closeDate',
      headerName: 'Close Date',
      flex: 1,
      minWidth: 120,
      valueGetter: (_value, row) => toSortableTimestamp(row.sales_and_forecast?.closedate),
      renderCell: ({ row }) => formatSyncedDay(row.sales_and_forecast?.closedate) ?? '—',
      ...dateOpts,
    },
    {
      field: 'owner',
      headerName: 'Owner Full Name',
      flex: 1,
      minWidth: 160,
      valueGetter: (_value, row) => row.opportunity_info?.opp_owner_lookup_name__c,
      ...sharedOpts,
    },
    {
      field: 'dealRunDate',
      headerName: 'Deal Run Date',
      flex: 1,
      minWidth: 160,
      valueGetter: (_value, row) => toSortableTimestamp(row.run_dates?.deal_run_date__c),
      renderCell: ({ row }) => formatSyncedDate(row.run_dates?.deal_run_date__c) ?? '—',
      ...dateOpts,
    },
    {
      field: 'plannedEndDate',
      headerName: 'Planned End Date',
      flex: 1,
      minWidth: 140,
      valueGetter: (_value, row) => toSortableTimestamp(row.contract_and_commercial?.planned_end_datele__c),
      renderCell: ({ row }) => formatSyncedDay(row.contract_and_commercial?.planned_end_datele__c) ?? '—',
      ...dateOpts,
    },
    {
      field: 'id',
      headerName: '18 Character ID',
      flex: 1,
      minWidth: 180,
      ...sharedOpts,
    },
    {
      field: 'leOfferId',
      headerName: 'LE Offer ID',
      flex: 1,
      minWidth: 120,
      valueGetter: (_value, row) => row.opportunity_info?.le_offer_id__c,
      ...sharedOpts,
    },
  ];
}

export default function AccountOpportunitiesListPage() {
  const { id_account: accountId } = useParams<{ id_account: string }>();
  const { data: account } = useGetAccount(accountId);
  const { data: opportunities, isLoading, error } = useGetAccountOpportunities(accountId);
  const { data: offerIds, error: offerIdsError } = useGetAccountOfferIds(accountId);
  const rows = useMemo(() => sortOpportunitiesByLatestDate(opportunities ?? []), [opportunities]);
  const columns = useMemo(() => getColumns(offerIds), [offerIds]);

  return (
    <>
      <Helmet>
        <title>Opportunities | {account?.name || accountId}</title>
      </Helmet>

      <AccountListPageHeader
        accountId={accountId}
        count={opportunities?.length}
        sortedBy="latest date"
        title="Opportunities"
      />

      {error ? (
        <Alert severity="error">Could not load opportunities: {error.message}</Alert>
      ) : (
        <>
          {offerIdsError && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {OFFER_LINKS_UNAVAILABLE_MESSAGE}
            </Alert>
          )}
          <DataGrid
            autoHeight
            disableRowSelectionOnClick
            columns={columns}
            getRowHeight={getAutoRowHeight}
            loading={isLoading}
            rows={rows}
            initialState={{ pagination: { paginationModel: { pageSize: 100 } } }}
            pageSizeOptions={[25, 50, 100]}
            sx={{ '& .MuiDataGrid-cell': { alignItems: 'center', display: 'flex', py: 1, whiteSpace: 'normal' } }}
          />
        </>
      )}
    </>
  );
}
